import { Fragment, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, History, RotateCcw } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import DateInput from '../components/DateInput'
import Field from '../components/Field'
import LoadingSpinner from '../components/LoadingSpinner'
import Select from '../components/Select'
import SettingsTabs from '../components/SettingsTabs'
import { formatDateTime, getInitials } from '../lib/utils'
import { AUDIT_ENTITY_TYPES } from '../types'
import type { AuditEntityType, AuditLogEntry, User } from '../types'

const PAGE_SIZE = 25

// Past this, the Change cell shows a preview and a toggle for the rest. One
// pasted paragraph would otherwise push every other row off the screen, and
// scanning the rows is the whole point of the page.
const PREVIEW_CHARS = 90

const ENTITY_LABELS: Record<AuditEntityType, string> = {
  project: 'Project',
  product: 'Product',
  settings: 'Settings',
  user: 'User',
  reporting_window: 'Reporting window',
  board_date_rules: 'Board date rules',
  role_group_map: 'Role groups',
  drumbeat_template: 'Drumbeat template',
}

// The verbs services/audit.py actually writes. Only the three worth telling
// apart at a glance are tinted; anything else a writer records still renders,
// in the neutral chip.
const ACTION_STYLES: Record<string, string> = {
  create: 'bg-[#00843D]/10 text-[#00843D]',
  update: 'bg-danone-blue/10 text-danone-blue',
  delete: 'bg-red-50 text-red-600',
}

// Verbs whose stored form is not what a reader should see. Anything absent
// falls back to a capitalised copy of whatever was recorded.
const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  roles_changed: 'Roles changed',
  ddi_closed: 'Closed DDI texts',
  ddi_reopened: 'Reopened DDI texts',
  ddi_reset: 'Reset the DDI slide',
  ddi_texts: 'Wrote DDI texts',
  ddi_drafted: 'Drafted DDI texts with AI',
}

const entityOptions = AUDIT_ENTITY_TYPES.map(type => [type, ENTITY_LABELS[type]] as const)

const pageButtonClass = 'px-2.5 py-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent'

const actionLabel = (action: string) =>
  ACTION_LABELS[action] || action.charAt(0).toUpperCase() + action.slice(1)

/** Audited values span strings, numbers, dates and small lists, so anything
 *  that is not already text is shown as its JSON. Text is left exactly as it
 *  was recorded. `-` is the house marker for a value that was not there. */
const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'string') return value
  return JSON.stringify(value) ?? '-'
}

const preview = (value: string) =>
  value.length > PREVIEW_CHARS ? `${value.slice(0, PREVIEW_CHARS)}...` : value

function Actor({ entry }: { entry: AuditLogEntry }) {
  const name = entry.actorName || entry.actorEmail || 'Unknown'
  return (
    <div className="flex items-center gap-2.5 min-w-0" title={entry.actorEmail || undefined}>
      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[11px] flex-shrink-0">
        {getInitials(entry.actorEmail || entry.actorName || '??')}
      </div>
      <span className="font-medium text-slate-800 truncate">{name}</span>
    </div>
  )
}

function ValueBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-2.5 whitespace-pre-wrap break-words">
        {value}
      </div>
    </div>
  )
}

/**
 * The central audit log. Platform admin only on both sides: this page redirects
 * anyone else, and `GET /audit` refuses them with a 403.
 *
 * It answers one question - who changed this, and when - so the actor and the
 * entity are what the row leads with, and the field's before/after is the
 * detail underneath.
 */
export default function SettingsAudit() {
  useDocumentTitle('Audit Log')
  const api = useApi()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isAdmin = !!user?.isAdmin

  // Same gate as the other settings pages: bounce once auth has resolved, so
  // the page never flashes during the initial MSAL settle.
  useEffect(() => {
    if (!authLoading && !isAdmin) navigate('/settings', { replace: true })
  }, [authLoading, isAdmin, navigate])

  const [entityType, setEntityType] = useState('')
  const [actorOid, setActorOid] = useState('')
  const [since, setSince] = useState('')
  const [page, setPage] = useState(0)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // A rejected fetch and an empty log both land as null in useData, and telling
  // an admin "nothing has been audited" when the call merely failed is the one
  // wrong answer this page can give. Catch into an explicit sentinel.
  const { data, loading } = useData(
    () => api.getAuditLog({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      entityType: (entityType || undefined) as AuditEntityType | undefined,
      actorOid: actorOid || undefined,
      since: since || undefined,
    }).catch(() => null),
    [entityType, actorOid, since, page],
  )

  // The actor filter reads the directory rather than the rows on screen: an
  // actor whose changes are all on page four would otherwise be unpickable.
  const { data: users } = useData(() => (api.getUsers() as Promise<User[]>).catch(() => []))

  if (authLoading || !isAdmin) return <LoadingSpinner />

  const actorOptions = [...(users || [])]
    .map(u => [u.oid, u.name || u.email || u.oid] as const)
    .sort((a, b) => a[1].localeCompare(b[1]))

  const filtered = !!entityType || !!actorOid || !!since
  const clearFilters = () => {
    setEntityType('')
    setActorOid('')
    setSince('')
    setPage(0)
    setExpandedId(null)
  }
  // Every filter change invalidates the offset, so they all reset to page one.
  const applyFilter = (set: (value: string) => void) => (value: string) => {
    set(value)
    setPage(0)
    setExpandedId(null)
  }
  const goToPage = (next: number) => {
    setPage(next)
    setExpandedId(null)
  }

  const entries = data?.entries || []
  const total = data?.total || 0
  const firstShown = page * PAGE_SIZE + 1
  const lastShown = page * PAGE_SIZE + entries.length
  const hasNext = lastShown < total

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>

      <SettingsTabs active="audit" />

      <div className="card p-6">
        <h2 className="font-bold text-slate-800 mb-1 flex items-center gap-1.5">
          <History className="w-4 h-4 text-slate-500" />
          Audit log
        </h2>
        <p className="text-sm text-slate-500 mb-5">
          Every recorded change across projects, products, settings, users, reporting windows and the board date rules, newest first. Objects keep their own history on their own page; this is the one place that spans all of them.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Entity">
            <Select
              value={entityType}
              onChange={applyFilter(setEntityType)}
              options={entityOptions}
              placeholder="All entities"
            />
          </Field>
          <Field label="Changed by">
            <Select
              value={actorOid}
              onChange={applyFilter(setActorOid)}
              options={actorOptions}
              placeholder="Anyone"
              searchable
            />
          </Field>
          <Field label="Changed since">
            <DateInput value={since} onChange={applyFilter(setSince)} />
          </Field>
        </div>

        {filtered && (
          <button
            type="button"
            onClick={clearFilters}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear filters
          </button>
        )}

        <div className="mt-6">
          {loading ? (
            <LoadingSpinner />
          ) : data === null ? (
            <p className="text-sm text-slate-400 text-center py-8">
              The audit log could not be loaded. Refresh the page to try again.
            </p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">
              {filtered
                ? 'No changes match these filters.'
                : 'Nothing has been audited yet. Changes appear here as they are made.'}
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-3 text-left font-semibold w-[12%]">When</th>
                      <th className="py-2 px-3 text-left font-semibold w-[18%]">Who</th>
                      <th className="py-2 px-3 text-left font-semibold w-[22%]">What</th>
                      <th className="py-2 px-3 text-left font-semibold w-[10%]">Action</th>
                      <th className="py-2 pl-3 text-left font-semibold w-[38%]">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(entry => {
                      const before = formatValue(entry.before)
                      const after = formatValue(entry.after)
                      // A create has nothing before it, and "- → x" reads
                      // worse than just "x".
                      const hadBefore = entry.before !== null && entry.before !== undefined
                      const long = (hadBefore && before.length > PREVIEW_CHARS) || after.length > PREVIEW_CHARS
                      const expanded = expandedId === entry.id
                      const entityLabel = ENTITY_LABELS[entry.entityType] || entry.entityType
                      const entityName = entry.entityName || entry.entityId
                      return (
                        <Fragment key={entry.id}>
                          <tr className={`align-top ${expanded ? '' : 'border-b border-slate-100'}`}>
                            <td className="py-3 pr-3 text-slate-500 whitespace-nowrap">{formatDateTime(entry.at)}</td>
                            <td className="py-3 px-3">
                              <Actor entry={entry} />
                            </td>
                            <td className="py-3 px-3 min-w-0">
                              <div className="font-medium text-slate-800 break-words">{entityName || entityLabel}</div>
                              {entityName && entityName !== entityLabel && (
                                <div className="text-xs text-slate-400">{entityLabel}</div>
                              )}
                            </td>
                            <td className="py-3 px-3 whitespace-nowrap">
                              <span className={`px-2 py-0.5 text-[11px] rounded-full font-semibold ${ACTION_STYLES[entry.action] || 'bg-slate-100 text-slate-600'}`}>
                                {actionLabel(entry.action)}
                              </span>
                            </td>
                            <td className="py-3 pl-3 min-w-0">
                              {entry.field && (
                                <span className="font-mono text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded break-all">{entry.field}</span>
                              )}
                              <div className="text-xs text-slate-500 break-words mt-1">
                                {hadBefore && (
                                  <>
                                    <span className="line-through text-slate-400">{preview(before)}</span>
                                    {' → '}
                                  </>
                                )}
                                <span>{preview(after)}</span>
                              </div>
                              {long && (
                                <button
                                  type="button"
                                  onClick={() => setExpandedId(expanded ? null : entry.id)}
                                  aria-expanded={expanded}
                                  className="mt-1.5 text-xs font-semibold text-danone-blue hover:underline"
                                >
                                  {expanded ? 'Hide full value' : 'Show full value'}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="border-b border-slate-100">
                              <td colSpan={5} className="pb-4 px-3">
                                <div className="grid gap-3 sm:grid-cols-2">
                                  {hadBefore && <ValueBlock label="Before" value={before} />}
                                  <ValueBlock label="After" value={after} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                <p className="text-xs text-slate-500">
                  Showing {firstShown} to {lastShown} of {total} {total === 1 ? 'change' : 'changes'}
                </p>
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <button
                    type="button"
                    aria-label="Previous page"
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 0}
                    className={pageButtonClass}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Next page"
                    onClick={() => goToPage(page + 1)}
                    disabled={!hasNext}
                    className={pageButtonClass}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
